import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Server, Monitor, Smartphone, Activity, Briefcase, Globe, Network, MessageSquare } from 'lucide-react';
import useStore from '../store';

const TreeNode = ({ label, subLabel, icon: Icon, children, isOpen, onToggle, actions }) => (
    <div className="select-none">
        <div
            className={`flex items-center gap-2 p-1.5 hover:bg-gray-700 rounded cursor-pointer text-sm ${actions ? '' : 'text-gray-300'}`}
            onClick={onToggle}
        >
            {children && (
                <span className="text-gray-500">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
            )}
            {!children && <span className="w-3.5" />} {/* Indent spacer */}

            {Icon && (typeof Icon === 'function' || typeof Icon === 'object') && <Icon size={14} className="text-blue-400" />}

            <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{label}</div>
                {subLabel && <div className="text-xs text-gray-500 truncate">{subLabel}</div>}
            </div>

            {actions}
        </div>

        {isOpen && children && (
            <div className="pl-4 border-l border-gray-700 ml-2 mt-1 space-y-1">
                {children}
            </div>
        )}
    </div>
);

const NetworkTree = ({ data, selectedId, onSelect }) => {
    return (
        <div className="space-y-1">
            {data.map(probe => (
                <div
                    key={probe.id}
                    onClick={() => onSelect(probe.id)}
                    className={`flex items-center gap-2 p-2 rounded cursor-pointer text-xs transition-colors ${selectedId === probe.id ? 'bg-blue-900/50 text-blue-100 border border-blue-700/50' : 'hover:bg-gray-800 text-gray-400 border border-transparent'}`}
                >
                    <Activity size={14} className={selectedId === probe.id ? 'text-green-400' : 'text-gray-600'} />
                    <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-sm">{probe.label}</div>
                        <div className="truncate text-[10px] text-gray-500">{probe.subLabel}</div>
                    </div>
                </div>
            ))}
            {data.length === 0 && (
                <div className="text-center p-4 text-gray-600 text-xs italic">
                    No probes connected
                </div>
            )}
        </div>
    );
};

const BusinessTree = ({ data, onSelectSession }) => {
    const [expanded, setExpanded] = useState({});
    const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

    return (
        <div className="space-y-2">
            {data.map(cat => (
                <TreeNode
                    key={cat.id}
                    label={cat.label}
                    icon={Briefcase}
                    isOpen={expanded[cat.id] || true} // Default open categories
                    onToggle={() => toggle(cat.id)}
                >
                    {cat.children.map(platform => (
                        <TreeNode
                            key={platform.id}
                            label={platform.label}
                            icon={Globe}
                            isOpen={expanded[platform.id]}
                            onToggle={() => toggle(platform.id)}
                        >
                            {platform.children.map((session, idx) => {
                                let faviconUrl = session.favIconUrl;
                                try {
                                    if (!faviconUrl && session.url) {
                                        faviconUrl = `https://www.google.com/s2/favicons?domain=${new URL(session.url).hostname}`;
                                    }
                                } catch {
                                    faviconUrl = null;
                                }
                                return (
                                    <div
                                        key={idx}
                                        onClick={() => onSelectSession(session.probeId, session.id)}
                                        className="flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs ml-4 hover:bg-gray-700 text-gray-300 group"
                                    >
                                        {faviconUrl && (
                                            <img
                                                src={faviconUrl}
                                                className="w-3 h-3 rounded-sm"
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate">{session.title || session.url || 'Unknown'}</div>
                                            <div className="text-[10px] text-gray-500 truncate group-hover:text-gray-400">
                                                {session.probeIp}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </TreeNode>
                    ))}
                </TreeNode>
            ))}
        </div>
    );
};

const Sidebar = ({ onSelectTarget, selectedTargetId, viewMode, setViewMode }) => {
    const { onlineTargets } = useStore();

    // Transform onlineTargets to networkData with safety check
    const safeOnlineTargets = onlineTargets || [];
    const networkData = safeOnlineTargets.map(t => {
        const ip = t.metadata?.clientIp || 'Unknown IP';
        const location = t.metadata?.ipLocation || '';
        const shopName = t.metadata?.shopName || t.metadata?.platform || '';
        const subLabelParts = [ip, location, shopName].filter(Boolean);
        return {
            id: t.id,
            label: String(t.id || '').substring(0, 20) + (String(t.id || '').length > 20 ? '...' : ''),
            subLabel: subLabelParts.join(' - ')
        };
    });

    // Placeholder for business data logic
    const businessData = [];

    return (
        <div className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col h-full">
            {/* View Switcher */}
            <div className="p-2 border-b border-gray-800 flex flex-col gap-2">
                {/* Doudian - Primary Button */}
                <button
                    onClick={() => setViewMode('DOUDIAN')}
                    className={`w-full py-2.5 px-3 text-xs font-medium rounded-lg flex items-center gap-3 transition-all ${viewMode === 'DOUDIAN' ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                >
                    <MessageSquare size={16} />
                    <span>抖店工作台</span>
                </button>

                {/* Network & Business Row */}
                <div className="flex gap-2">
                    <button
                        onClick={() => setViewMode('NETWORK')}
                        className={`flex-1 py-1.5 px-2 text-xs font-medium rounded flex items-center justify-center gap-2 transition-colors ${viewMode === 'NETWORK' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    >
                        <Network size={14} /> Network
                    </button>
                    <button
                        onClick={() => setViewMode('BUSINESS')}
                        className={`flex-1 py-1.5 px-2 text-xs font-medium rounded flex items-center justify-center gap-2 transition-colors ${viewMode === 'BUSINESS' ? 'bg-green-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}
                    >
                        <Briefcase size={14} /> Business
                    </button>
                </div>
            </div>

            {/* Tree View Content */}
            <div className="flex-1 overflow-y-auto p-2 scrollbar-thin scrollbar-thumb-gray-700">
                {viewMode === 'NETWORK' ? (
                    <NetworkTree
                        data={networkData}
                        selectedId={selectedTargetId}
                        onSelect={onSelectTarget}
                    />
                ) : (
                    <BusinessTree
                        data={businessData}
                        onSelectSession={() => { }}
                    />
                )}
            </div>
        </div>
    );
};

export default Sidebar;
